import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import CustomInput from "../shared/custom-input/custom-input";
import CustomButton from "../shared/custom-button/custom-button";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { SnackbarContext } from "../config/hooks/use-toast";

export interface FormModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: FormData;
  modalTitle: string;
  buttonText: string;
  onButtonClick: any;
  onImageChange?: (imageSrc: string) => void;
}

interface FormData {
  image: string;
  title: string;
  subTitle: string;
  description: string;
  price: number | string;
  monthlyPeriod: number;
  offerDetails: string;
  classNumber: number;
}

const defaultData: FormData = {
  image: "",
  title: "",
  subTitle: "",
  description: "",
  price: "",
  monthlyPeriod: 1,
  offerDetails: "",
  classNumber: 0,
};

const FormModal: React.FC<FormModalProps> = ({
  open,
  onClose,
  initialData = defaultData,
  modalTitle,
  buttonText,
  onButtonClick,
  onImageChange,
}) => {
  const snackbarContext = useContext(SnackbarContext);

  const [sendeData, setSendeData] = useState<FormData>(initialData);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [benefitInput, setBenefitInput] = useState<string>("");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "price") {
      const parsed = Number(value);
      setSendeData((prev) => ({
        ...prev,
        price: Number.isFinite(parsed) ? parsed : prev.price,
      }));
      return;
    }

    if (name === "monthlyPeriod") {
      const parsed = Number(value);
      setSendeData((prev) => ({
        ...prev,
        monthlyPeriod: Number.isFinite(parsed) && parsed > 0 ? parsed : prev.monthlyPeriod,
      }));
      return;
    }

    if (name === "classNumber") {
      const parsed = Number(value);
      setSendeData((prev) => ({
        ...prev,
        classNumber: Number.isFinite(parsed) && parsed >= 0 ? parsed : prev.classNumber,
      }));
      return;
    }

    setSendeData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImageFile(file); // Store the file itself
        if (onImageChange) {
          onImageChange(result);
        }
      };
      reader.readAsDataURL(file);
    } else {
      console.error("No file selected or file is invalid.");
    }
  };

  const handleAddBenefit = () => {
    if (benefits.length < 5) {
      if (benefitInput.trim() && benefitInput.length < 30) {
        setBenefits([...benefits, benefitInput.trim()]);
        setBenefitInput("");
      }
    }
  };

  const handleRemoveBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const handleActionClick = () => {
    const formData = new FormData();
    const { image, ...selectedUser } = sendeData;

    const offerDetails = benefits.join(" \n ");
    const normalizedPrice =
      Number.isFinite(Number(sendeData.price)) && Number(sendeData.price) >= 0
        ? Number(sendeData.price)
        : 0;
    const normalizedMonthlyPeriod =
      Number.isFinite(Number(sendeData.monthlyPeriod)) && Number(sendeData.monthlyPeriod) > 0
        ? Number(sendeData.monthlyPeriod)
        : 1;
    const normalizedClassNumber =
      Number.isFinite(Number(sendeData.classNumber)) && Number(sendeData.classNumber) > 0
        ? Number(sendeData.classNumber)
        : 0;

    formData.append(
      "teacherOfferDTOJson",
      JSON.stringify({
        ...selectedUser,
        price: normalizedPrice,
        monthlyPeriod: normalizedMonthlyPeriod,
        classNumber: normalizedClassNumber,
        offerDetails,
      }),
    );
    if (imageFile) {
      formData.append("image", imageFile);
    }
    if (!imageFile || !sendeData.image) {
      if (snackbarContext) {
        snackbarContext.showMessage(
          "Erreur",
          "Veuiller importer une image",
          "error",
        );
      }
    }

    onButtonClick(formData);
  };

  useEffect(() => {
    if (open) {
      setSendeData(initialData);
      setImageFile(null); // reset image preview so the dropzone is always visible on open
      setBenefits(
        initialData.offerDetails ? initialData.offerDetails.split("\n") : [],
      );
    }
  }, [open, initialData]);

  return (
    <Modal
      open={open}
      onClose={() => {
        setSendeData(defaultData);
        setImageFile(null);
        onClose();
      }}
      className="flex justify-end backdrop-blur-sm bg-title/20"
      closeAfterTransition
    >
      <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Drawer Header */}
        <div className="p-8 pb-6 flex items-center justify-between border-b border-gray-50 bg-white/50 backdrop-blur-md flex-shrink-0 z-30">
          <div>
            <h2 className="text-2xl font-black text-title uppercase tracking-tighter">{modalTitle}</h2>
            <p className="text-[9px] font-black text-primary uppercase tracking-[0.4em] mt-1">Configuration de l'offre professeur</p>
          </div>
          <button 
            onClick={() => { setSendeData(defaultData); setImageFile(null); onClose(); }}
            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all duration-300 group cursor-pointer"
          >
             <span className="text-2xl font-light group-hover:rotate-90 transition-transform duration-500">&times;</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-10 bg-white">
          {/* Section: Cover Illustration */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
               <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
               Image de couverture
            </h3>
            <label className="group relative block w-full aspect-video rounded-[2.5rem] overflow-hidden cursor-pointer bg-slate-50 border-2 border-dashed border-slate-100 hover:border-primary/40 transition-all duration-700">
              {imageFile || sendeData.image ? (
                <>
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : sendeData.image}
                    alt="Preview"
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-title/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center">
                    <div className="flex flex-col items-center text-white space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-2xl border border-white/20">
                         <AddAPhotoIcon className="text-xl" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">Remplacer l'image</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
                  <div className="w-20 h-20 bg-white rounded-3xl shadow-xl shadow-slate-200/50 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-700">
                    <AddAPhotoIcon className="text-2xl" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400">Importer un visuel</span>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </section>

          {/* Section: Main Details */}
          <section className="space-y-8">
             <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Informations Générales
                </label>
                <div className="grid grid-cols-1 gap-6">
                  <CustomInput
                    placeholder="Titre de l'offre"
                    name="title"
                    value={sendeData.title}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  />
                  <CustomInput
                    placeholder="Sous-titre"
                    name="subTitle"
                    value={sendeData.subTitle}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  />
                  <CustomInput
                    placeholder="Description complète"
                    name="description"
                    value={sendeData.description}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-medium !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  />
                </div>
             </div>

             {/* Section: Pricing & Duration */}
             <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Tarification & Structure
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <CustomInput
                    placeholder="Prix (DT)"
                    name="price"
                    inputType="number"
                    value={sendeData.price}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm"
                  />
                  <CustomInput
                    placeholder="Durée (Mois)"
                    name="monthlyPeriod"
                    inputType="number"
                    value={sendeData.monthlyPeriod}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm"
                  />
                  <div className="col-span-2">
                    <CustomInput
                      placeholder="Nombre de Classes autorisées"
                      name="classNumber"
                      inputType="number"
                      value={sendeData.classNumber}
                      onChange={handleChange}
                      inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm"
                    />
                  </div>
                </div>
             </div>

             {/* Section: Benefits */}
             <div className="space-y-4">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Avantages inclus
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <CustomInput
                      placeholder="E.g. Support personnalisé 24/7"
                      value={benefitInput}
                      onChange={(e) => setBenefitInput(e.target.value)}
                      inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-medium"
                    />
                  </div>
                  <button 
                    onClick={handleAddBenefit}
                    className="w-16 h-16 bg-primary text-white rounded-2xl shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center flex-shrink-0"
                  >
                    <AddCircleIcon />
                  </button>
                </div>

                <div className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-white hover:border-primary/20 transition-all group">
                      <span className="text-[11px] font-bold text-title uppercase tracking-wider">{benefit}</span>
                      <button onClick={() => handleRemoveBenefit(index)} className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-rose-500 transition-all">
                        <RemoveCircleIcon fontSize="small" />
                      </button>
                    </div>
                  ))}
                  {benefits.length === 0 && (
                    <div className="py-8 text-center border-2 border-dashed border-slate-50 rounded-[2rem]">
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Aucun avantage ajouté</p>
                    </div>
                  )}
                </div>
             </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="p-8 bg-white border-t border-gray-100 flex gap-4 z-40 flex-shrink-0">
           <button 
              onClick={() => { setSendeData(defaultData); setImageFile(null); onClose(); }}
              className="px-8 h-14 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 hover:bg-rose-50 active:scale-95 transition-all duration-300 pointer-events-auto"
           >
              Annuler
           </button>
           <button 
              onClick={handleActionClick}
              className="flex-1 h-14 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4"
           >
              {buttonText}
           </button>
        </div>
      </div>
    </Modal>
  );
};

export default FormModal;
